import { concat, isEmpty, map, mapKeys, unionBy } from 'lodash';
import { COLOR_PALETTES } from '../../../Constants';
import Filters from './Filters';

const CSV_LINKS_DEFINITON_COLUMN_NAME = 'Definiciones';

const CSV_NODE_COLUMN_NAME = 'Ítems';
const CSV_CATEGORY_COLUMN_NAME = 'Agrupación';
const CSV_SHORT_NAME='Nombre Corto';
const CSV_TOOLTIP_ENABLED = 'Tooltip';

const CSV_ADDONS_NODE_COLUMN_NAME = 'Agregaciones';
const CSV_ADDONS_ADDON_COLUMN_NAME = 'Definiciones';


function getLinksData(csvUrl, weightColName){
    return new Promise((resolve, reject)=>{
        window.d3.csv(csvUrl, function (error, rows){
            if(!error){
                const cleanRows = cleanKeysOfArray(rows);
                const data = parseData(cleanRows, weightColName);
                resolve(data);
            }else{
                reject(error);
            }
        });
    });
}

function getCategoriesData(csvUrl){
    return new Promise((resolve, reject)=>{
        window.d3.csv(csvUrl, function(error, categoryRows){
            if(!error){
                const cleanCategoryRows = cleanKeysOfArray(categoryRows);
                resolve(cleanCategoryRows);
            }else{
                reject(error);
            }
        });
    });
}

function getAddonsData(csvUrl){
    return new Promise((resolve, reject)=>{
        window.d3.csv(csvUrl, function(error, addonRows){
            if(!error){
                resolve(addonRows);
            }else{
                reject(error);
            }
        });
    });
}

// remove spaces before and after the object's keys (cell headers on spreadsheet or xlsx)
function cleanKeysOfArray(objects){
    const cleanArray = map(objects, (obj) => {
    
        return mapKeys(obj, (value, key) => {
            return key.trim();
        });
    });

    return cleanArray;
}

function parseData(rows, weightColName) {

    let nodes = [];
    const links = [];

    // add nodes
    const nodeRows = rows.filter(r=>r[weightColName]==1 && !isEmpty(r.A) && !isEmpty(r.B));

    for (const row of nodeRows) {
        // add to nodes if does not exist
        const node_A = {
            name: clean(row.A)
        };
        const node_B = {
            name: clean(row.B)
        };
        nodes = unionBy(nodes,[node_A, node_B], 'name');
    }
    nodes.forEach((node, index)=>{
        node.id = index;
    });

    // add link
    for (const row of nodeRows) {
        const sourceNode = nodes.find(node => clean(node.name) === clean(row.A));
        const targetNode = nodes.find(node => clean(node.name) === clean(row.B));

        if (sourceNode && targetNode) {
            links.push({
                ...row,
                source: sourceNode.id,
                target: targetNode.id,
                value: 1,
                definition: row[CSV_LINKS_DEFINITON_COLUMN_NAME]
            });
        }
    }

    return {
        nodes, 
        links
    };
}

function plainArray(items){
    let array = [];
    for(const item of items){
        if(item.subgroup && item.subgroup.length>0){
            array = concat(array, item.subgroup);
        }else{
            array = concat(array, [item]);
        }
    }
    return array;
}

async function buildGraphData(graphRecord, workWidth, workHeight, padding, cellHeight, sankey){

    let categories = plainArray(graphRecord.template.groups);

    // set type filter
    const graphTypeFilter = graphRecord.template.filters.find(filter=>filter.type===Filters.NAMES.GRAPH_TYPE);
    let weightColName = graphTypeFilter.options[0].name; // default the first one

    if(graphRecord.selectedGraphType){
        weightColName = graphRecord.selectedGraphType;
    }

    const data = await getLinksData(graphRecord.linksFile, weightColName);
    const groupingRows = await getCategoriesData(graphRecord.groupsFile);
    let addonsRows = [];
    if(graphRecord.addonsFile){
        addonsRows = await getAddonsData(graphRecord.addonsFile);
    }
    
    // assign category to nodes
    data.nodes.forEach((node, index)=>{

        node.id = `node-${index}`;

        // grouping /category
        const foundGroupingRow = groupingRows.find(row => clean(row[CSV_NODE_COLUMN_NAME]) === clean(node.name));
        if(foundGroupingRow){
            const group = categories.find(c => clean(c.name) === clean(foundGroupingRow[CSV_CATEGORY_COLUMN_NAME]));
            node.category = group;
            node.shortName = foundGroupingRow[CSV_SHORT_NAME];
            node.tooltip = foundGroupingRow[CSV_TOOLTIP_ENABLED] == 1 ? foundGroupingRow[CSV_NODE_COLUMN_NAME]: null;
        }else{
            node.category = {};
        }

        const colorScale = window.d3.scaleOrdinal().domain(window.d3.range(0, data.nodes.length)).range(node.category.colors);
        node.color = colorScale(index);

        // addons
        const foundAddon = addonsRows.find(a => clean(a[CSV_ADDONS_NODE_COLUMN_NAME]) === clean(node.name));
        if(foundAddon){
            node.definition = foundAddon[CSV_ADDONS_ADDON_COLUMN_NAME];
        }
    });

    //assign colors to links
    const linksColorScale = window.d3.scaleOrdinal().range(COLOR_PALETTES.LINKS || window.d3.schemeCategory20);
    data.links.forEach((link, index)=>{
        link.color = linksColorScale(index)
    });

    // calculate weigh on each category to use a weighted width on canvas
    // calculateCategoryWeights(3, data.nodes, categories);
    // replace calculated weights by template props

    if(graphRecord.metadata && graphRecord.metadata.nodesDetails){
        // set new node locations based on previous stored location
        const nodesDetails = graphRecord.metadata.nodesDetails;
        data.nodes.forEach(node=>{
            const foundNodeDetail = nodesDetails.find(nd=>nd.name === node.name);
            if(foundNodeDetail && foundNodeDetail && foundNodeDetail.x && foundNodeDetail.y){
                node.newX = foundNodeDetail.x;
                node.newY = foundNodeDetail.y;
                node.textAnchor = foundNodeDetail.textAnchor;
            }
        });

        // update positions based on canvas size (factor scaling)
        const w1 = workWidth + padding.left + padding.right;
        const w0 = graphRecord.canvasWidth;
        const kw = w1/w0;

        data.nodes.forEach(node=>{
            node.newX = node.newX * kw;
        });  
    }else{
        // change nodes location based on category
        let x = 0;
        let y = 0;
    
        categories.forEach((category, index) => {

            const categoryNodes = data.nodes.filter(node=>node.category.name === category.name);
            const categoryWidth = category.weight * workWidth;

            let cx = padding.left;
            let cy = padding.top;
            for(const node of categoryNodes){

                const limit_x = x + (categoryWidth - padding.right);
                if((x + cx + sankey.nodeWidth()) > limit_x){ // back to category x - origin if overflows the category space
                    cx = padding.left;
                }

                const verticalOffset = category.align === 'bottom'? workHeight/2 : 0;

                node.newX = x + cx;
                node.newY = y + cy + verticalOffset;

                
                cy = cy + cellHeight;
                cx = cx + sankey.nodeWidth() + sankey.nodePadding();

            }

            if(category.align !== 'top'){
                x = x + categoryWidth;
            }
        });
    }

    // labels

    if(graphRecord.metadata && graphRecord.metadata.labelsDetails){
        for(const category of categories){
            const labelDetail = graphRecord.metadata.labelsDetails.find(ld=>ld.categoryName===category.name);
            if(labelDetail){
                category.labelDetail = labelDetail;
            }
        }
    }

    return {
        graphData: data,
        categories: categories
    };    
}

// replaced by hardcoded values
function calculateCategoryWeights(maxNodesInColumn, nodes, categories){

    let totalWeight = 0;

    for(const category of categories){
        const categoryNodes = nodes.filter(n=>n.category.name===category.name);
        category.weight = Math.ceil(categoryNodes.length / maxNodesInColumn) + 2;
        totalWeight = totalWeight + category.weight;
    }

    // map height for each category
    for(const category of categories){
        category.weight = category.weight / totalWeight;
    }
}

function clean(data){
    if(!data){
        return data;
    }
    if(typeof data === 'string'){
        // remove \r
        return data.replace(/\r/g, "").trim();
    }else{
        return data;
    }
}

export {
    buildGraphData,
    clean
}
